/* eslint-disable react-hooks/exhaustive-deps */
import { MouseEventHandler, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import {
  Box,
  CircularProgress,
  Divider,
  Grid,
  Link,
  makeStyles,
  Typography,
} from "@material-ui/core";
import apiRequest from "utils/apiRequestWithErrorCode";
import { PropertyData, PropertyLabel } from "./styled";
import Title from "./Title";
import { fetchDeliveryDetails, getPostfixCodeFromEvent } from "./utils";

import { DeliveryDetails, EmailFlowLogDetails } from "@dashboard-v3/api";

interface Props {
  event: EmailFlowLogDetails;
}

type OriginalDeliveryInfo = DeliveryDetails & {
  recipient: string;
  messageId: string;
  createdAt: string;
};

export default function MailReject({ event }: Props) {
  const { action, data } = event;
  const { t } = useTranslation("tracing");
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [mailDelivered, setMailDelivered] = useState<EmailFlowLogDetails>();
  const [deliveryInfo, setDeliveryInfo] = useState<OriginalDeliveryInfo[]>([]);

  useEffect(() => {
    async function getOriginalMailDeliveredEvent() {
      const basePath = "/emailflow-logs/details";
      const params = new URLSearchParams();
      params.set("fromNotifications", "true");

      const events = await apiRequest<EmailFlowLogDetails[]>(
        "GET",
        `${basePath}/${data.notificationMsgUID}?${params.toString()}`
      );

      events.forEach(e => {
        if (e.action === "mail_delivered") {
          setMailDelivered(e);
        }
      });
    }

    if (!mailDelivered && data?.notificationMsgUID) {
      getOriginalMailDeliveredEvent();
    }
  }, []);

  const getDeliveryDetails: MouseEventHandler<HTMLAnchorElement> = async e => {
    e.preventDefault();
    setLoading(true);

    try {
      const recipient = (mailDelivered.data.recipient || "") as string;
      const code = getPostfixCodeFromEvent(mailDelivered);

      const details = await fetchDeliveryDetails({
        code,
        recipient,
        event: mailDelivered,
      });

      const deliveryDetails: OriginalDeliveryInfo[] = details.map(d => {
        return {
          ...d,
          recipient,
          messageId: mailDelivered.data?.messageId as string,
          createdAt: new Date(mailDelivered.createdAt).toDateString(),
        };
      });

      setDeliveryInfo(deliveryDetails);
    } catch (error) {
      console.error(error);
      enqueueSnackbar(t("common:errors.fetchError"), {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Title timestamp={event.createdAt}>
        <Typography style={{ fontWeight: "500" }}>
          {t(`eventActionType.${action}`)}
        </Typography>
      </Title>

      <Grid item xs={12}>
        <Grid container>
          <Grid item xs={4} style={{ marginBottom: "15px" }}>
            <PropertyLabel>{t("reason")}</PropertyLabel>
            <PropertyData color="textPrimary">
              {event.data.reason as string}
            </PropertyData>
          </Grid>
          {event.data?.notificationMsgUID && (
            <Grid item xs={4} style={{ marginBottom: "15px" }}>
              <PropertyLabel>{t("notificationMsgUID.label")}</PropertyLabel>
              <PropertyData color="textPrimary">
                {event.data.notificationMsgUID as string}
              </PropertyData>
            </Grid>
          )}
          {/*
          {event.data?.notificationOrganizationId && (
            <Grid item xs={4} style={{ marginBottom: "15px" }}>
              <PropertyLabel>{t("notificationOrgId.label")}</PropertyLabel>
              <PropertyData>
                {event.data.notificationOrganizationId as string}
              </PropertyData>
            </Grid>
          )}
          */}
        </Grid>
        {deliveryInfo.length === 0 && Boolean(mailDelivered) && (
          <div className={classes.shoInfoLinkContainer}>
            <Link href="#" onClick={getDeliveryDetails}>
              {t("delivered.showMore")}
            </Link>
            {loading && <CircularProgress size="1rem" color="inherit" />}
          </div>
        )}
        {deliveryInfo.length > 0 && (
          <>
            <PropertyLabel style={{ marginTop: 10, marginBottom: 15 }}>
              {t("deliveryInfo.label")}
            </PropertyLabel>
            {deliveryInfo.map((item, index) => (
              <Box ml={2} key={item.time}>
                <Grid
                  container
                  direction="row"
                  justifyContent="flex-start"
                  spacing={3}
                >
                  <Grid item>
                    <Box display="flex" flexDirection="column">
                      <Typography
                        color="textSecondary"
                        component="span"
                        variant="caption"
                      >
                        {t("recipient")}
                      </Typography>
                      <Box>{item.recipient}</Box>
                    </Box>
                  </Grid>
                  <Grid item>
                    <Box display="flex" flexDirection="column">
                      <Typography
                        color="textSecondary"
                        component="span"
                        variant="caption"
                      >
                        {t("createdAt")}
                      </Typography>
                      <Box>{item.createdAt}</Box>
                    </Box>
                  </Grid>
                  <Grid item>
                    <Box display="flex" flexDirection="column">
                      <Typography
                        color="textSecondary"
                        component="span"
                        variant="caption"
                      >
                        {t("messageId")}
                      </Typography>
                      <Box>{item.messageId}</Box>
                    </Box>
                  </Grid>
                  <Grid item>
                    <Box display="flex" flexDirection="column">
                      <Typography
                        color="textSecondary"
                        component="span"
                        variant="caption"
                      >
                        {t("delivered.at")}
                      </Typography>
                      <Box>{new Date(item.time).toLocaleString()}</Box>
                    </Box>
                  </Grid>
                  <Grid item>
                    <Box display="flex" flexDirection="column">
                      <Typography
                        color="textSecondary"
                        component="span"
                        variant="caption"
                      >
                        {t("delivered.delay")}
                      </Typography>
                      <Box>
                        {t("delivered.delaySeconds", { count: item.delay })}
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item>
                    <Box display="flex" flexDirection="column">
                      <Typography
                        color="textSecondary"
                        component="span"
                        variant="caption"
                      >
                        {t("delivered.relay")}
                      </Typography>
                      <Box>{item.relay}</Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box display="flex" flexDirection="column">
                      <Typography
                        color="textSecondary"
                        component="span"
                        variant="caption"
                      >
                        {t("delivered.status")}
                      </Typography>
                      <Box>{item.status}</Box>
                    </Box>
                  </Grid>
                </Grid>
                {index < deliveryInfo.length - 1 && (
                  <Box my={2}>
                    <Divider light />
                  </Box>
                )}
              </Box>
            ))}
          </>
        )}
      </Grid>
    </>
  );
}

const useStyles = makeStyles({
  shoInfoLinkContainer: {
    marginTop: 10,
    marginBottom: 10,
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
});
